/**
 * Инициализация модального окна: observers, отслеживание ввода, автофокус.
 */

/**
 * Инициализирует MutationObserver, ResizeObserver и обработчики ввода.
 * Вызывается из _init().
 * @param {ModalManager} manager
 */
export function initObservers(manager) {
    // MutationObserver для отслеживания изменений атрибутов и DOM-дерева
    const observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                if (manager.modalElement.classList.contains('show')) {
                    if (manager.type === 'modal') {
                        manager._makeDraggable();
                    }
                    setTimeout(() => setAutofocusInternal(manager), 150);
                }
            }

            if (mutation.type === 'childList') {
                if (mutation.addedNodes.length || mutation.removedNodes.length) {
                    setFocus(manager);
                }
            }
        }
    });

    // ResizeObserver для пересчёта высоты body
    const resizeObserver = new ResizeObserver(() => {
        manager._setModalBodyScroll();
    });

    observer.observe(manager.modalElement, {
        attributes: true,
        childList: true,
        subtree: true
    });

    if (manager.modalDialog) {
        resizeObserver.observe(manager.modalDialog);
    }

    // Обработчики ввода
    manager.modalElement.addEventListener('keyup', (event) => event.target.matches('input') && saveEditInput(manager, event));
    manager.modalElement.addEventListener('click', (event) => event.target.matches('input') && clearEditInput(manager));
    manager.modalElement.addEventListener('focusin', (event) => event.target.matches('input') && saveFocusInput(manager, event));
}

/**
 * Очищает сохранённое состояние редактируемого input.
 * @param {ModalManager} manager
 */
export function clearEditInput(manager) {
    manager._inputEdit = {};
}

/**
 * Сохраняет текущий input и позицию каретки.
 * @param {ModalManager} manager
 * @param {Event} event
 */
export function saveEditInput(manager, event) {
    const input = event.target;
    manager._inputEdit.target = input;
    manager._inputEdit.caretPosition = input.selectionStart;
}

/**
 * Сохраняет input при получении фокуса (с задержкой для корректной позиции каретки).
 * @param {ModalManager} manager
 * @param {Event} event
 */
export function saveFocusInput(manager, event) {
    setTimeout(() => {
        saveEditInput(manager, event);
    }, 0);
}

/**
 * Восстанавливает фокус и позицию каретки после обновления DOM.
 * @param {ModalManager} manager
 */
export function setFocus(manager) {
    if (manager._inputEdit.target) {
        const newActiveElement = manager.modalElement.querySelector(`[name="${manager._inputEdit.target.name}"]`);
        if (newActiveElement) {
            newActiveElement.focus();

            const allowedTypes = ['text', 'tel', 'password'];
            if (allowedTypes.includes(newActiveElement.type)) {
                newActiveElement.setSelectionRange(manager._inputEdit.caretPosition, manager._inputEdit.caretPosition);
            }
        }
    } else {
        setAutofocusInternal(manager);
    }
}

/**
 * Устанавливает фокус на первый элемент с атрибутом autofocus.
 * @param {ModalManager} manager
 */
export function setAutofocusInternal(manager) {
    const autofocusElement = manager.modalElement.querySelector('[autofocus]');
    if (autofocusElement) {
        setTimeout(() => {
            try {
                autofocusElement.focus();

                if (autofocusElement.tagName === 'INPUT') {
                    const allowedTypes = ['text', 'tel', 'password', 'email', 'url', 'search'];
                    if (allowedTypes.includes(autofocusElement.type) && autofocusElement.value) {
                        const length = autofocusElement.value.length;
                        autofocusElement.setSelectionRange(length, length);
                    }
                }

                if (autofocusElement.tagName === 'TEXTAREA' && autofocusElement.value) {
                    const length = autofocusElement.value.length;
                    autofocusElement.setSelectionRange(length, length);
                }

                if (manager.log) {
                    console.log('Автофокус установлен на элемент:', autofocusElement);
                }
            } catch (error) {
                if (manager.log) {
                    console.warn('Ошибка при установке автофокуса:', error);
                }
            }
        }, 100);
    } else if (manager.log) {
        console.log('Элемент с атрибутом autofocus не найден в модальном окне');
    }
}
